import { Routes, Route, useLocation } from "react-router-dom";
import "./App.css";
import "bootstrap/dist/css/bootstrap.min.css";
// import Home from "./pages/home";

import PrivacyPolicy from "./pages/PrivacyPolicy";
import TermsConditions from "./pages/TermsConditions";
import CookiePolicy from "./pages/CookiePolicy";
import ContactUs from "./pages/ContactUs";
import Product from "./pages/Product";
import SmartSchoolSystem from "./pages/SmartSchoolSystem";
import { useEffect } from "react";
import { setTranslations } from "./services/Helper";
// import { chatLoader } from "./services/syncLoader";

const BASE_URL = "https://www.esyncing.com";
const DEFAULT_OG_IMAGE =
  "https://pub-uploads.s3.amazonaws.com/uploads/chat/web/dk4JN4AVx9_esyncing-logo.png";

function App() {
  const location = useLocation();

  useEffect(() => {
    // chatLoader();
    setTranslations();
  }, []);

  useEffect(() => {
    const path = location.pathname || "/";
    const seo = {
      "/": {
        title: "Smart School System | eSyncing",
        description:
          "Explore SmartClass, a complete digital system for school communication, announcements, and learning resources.",
      },
      "/product": {
        title: "Product | eSyncing",
        description:
          "Explore eSyncing features: interactive demos, AI chat, HR assistant, knowledge hub, and integrations.",
      },
      "/smart-school-system": {
        title: "Smart School System | eSyncing",
        description:
          "Explore SmartClass, a complete digital system for school communication, announcements, and learning resources.",
      },
      "/contact-us": {
        title: "Contact Us | eSyncing",
        description:
          "Contact eSyncing for support, product questions, or partnership inquiries.",
      },
      "/privacy-policy": {
        title: "Privacy Policy | eSyncing",
        description:
          "Learn how eSyncing collects, uses, and protects your data.",
      },
      "/terms-conditions": {
        title: "Terms & Conditions | eSyncing",
        description:
          "Review eSyncing terms and conditions for platform use.",
      },
      "/cookie-policy": {
        title: "Cookie Policy | eSyncing",
        description:
          "Understand how eSyncing uses cookies and similar technologies.",
      },
    }[path] || {
      title: "eSyncing",
      description:
        "eSyncing is an AI-powered platform for interactive demos, knowledge, and support.",
    };

    const url = `${BASE_URL}${path}`;
    document.title = seo.title;

    const upsertMeta = (attr, key, value) => {
      if (!value) return;
      let el = document.querySelector(`meta[${attr}="${key}"]`);
      if (!el) {
        el = document.createElement("meta");
        el.setAttribute(attr, key);
        document.head.appendChild(el);
      }
      el.setAttribute("content", value);
    };

    upsertMeta("name", "description", seo.description);
    upsertMeta("name", "robots", "index,follow");
    upsertMeta("property", "og:title", seo.title);
    upsertMeta("property", "og:description", seo.description);
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", DEFAULT_OG_IMAGE);
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", seo.title);
    upsertMeta("name", "twitter:description", seo.description);
    upsertMeta("name", "twitter:image", DEFAULT_OG_IMAGE);

    let canonical = document.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", url);

    const ldId = "esyncing-jsonld";
    let ld = document.getElementById(ldId);
    if (!ld) {
      ld = document.createElement("script");
      ld.type = "application/ld+json";
      ld.id = ldId;
      document.head.appendChild(ld);
    }
    const jsonLdByPath = {
      "/": {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            name: "eSyncing",
            url: BASE_URL,
            logo: DEFAULT_OG_IMAGE,
            description:
              "eSyncing is an AI-powered platform for interactive demos, knowledge management, team communication, and digital service workflows.",
          },
          {
            "@type": "WebSite",
            name: "eSyncing",
            url: BASE_URL,
            potentialAction: {
              "@type": "SearchAction",
              target: `${BASE_URL}/?q={search_term_string}`,
              "query-input": "required name=search_term_string",
            },
          },
          {
            "@type": "SoftwareApplication",
            name: "eSyncing",
            applicationCategory: "BusinessApplication",
            operatingSystem: "Web",
            url: BASE_URL,
            description:
              "A web platform for interactive product demos, AI chat, knowledge hubs, and communication workflows for teams and schools.",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "USD",
            },
          },
          {
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "What does eSyncing do?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "eSyncing helps organizations create interactive demos, manage knowledge, improve communication, and support AI-powered workflows.",
                },
              },
              {
                "@type": "Question",
                name: "Who is eSyncing for?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "eSyncing is for product teams, support teams, internal operations teams, and schools that need better digital communication and knowledge access.",
                },
              },
              {
                "@type": "Question",
                name: "What services does eSyncing offer?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "eSyncing offers interactive product demos, AI chat, knowledge hubs, integrations, workflow support, and Smart School System solutions.",
                },
              },
            ],
          },
        ],
      },
      "/smart-school-system": {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            name: "eSyncing",
            url: BASE_URL,
          },
          {
            "@type": "Service",
            name: "Smart School System",
            serviceType: "School Communication and Learning Platform",
            provider: {
              "@type": "Organization",
              name: "eSyncing",
            },
            areaServed: "Worldwide",
            url: `${BASE_URL}/smart-school-system`,
            description:
              "SmartClass is a digital school system for communication, announcements, learning resources, and school-wide updates.",
          },
        ],
      },
      "/product": {
        "@context": "https://schema.org",
        "@graph": [
          {
            "@type": "Organization",
            name: "eSyncing",
            url: BASE_URL,
          },
          {
            "@type": "ItemList",
            name: "eSyncing Products and Services",
            itemListElement: [
              {
                "@type": "Service",
                position: 1,
                name: "Interactive Product Demos",
                description:
                  "Guided product experiences that help users understand software through interactive walkthroughs.",
              },
              {
                "@type": "Service",
                position: 2,
                name: "AI-Powered Chat for Teams and Customers",
                description:
                  "Real-time AI-assisted chat for support, internal collaboration, and customer communication.",
              },
              {
                "@type": "Service",
                position: 3,
                name: "Virtual HR Assistant",
                description:
                  "AI-supported HR help for employee questions, policy access, and internal guidance.",
              },
              {
                "@type": "Service",
                position: 4,
                name: "Company Knowledge Hub",
                description:
                  "Centralized knowledge management and AI-assisted search for internal documents and resources.",
              },
              {
                "@type": "Service",
                position: 5,
                name: "Integrations",
                description:
                  "Connected workflows and integrations across communication, email, and document tools.",
              },
              {
                "@type": "Service",
                position: 6,
                name: "Industry Use Cases",
                description:
                  "Solutions for SaaS, customer support, real estate, e-commerce, enterprise, and education teams.",
              },
            ],
          },
        ],
      },
    };
    const jsonLd =
      jsonLdByPath[path === "/" ? "/smart-school-system" : path] ||
      {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: "eSyncing",
        url: BASE_URL,
        potentialAction: {
          "@type": "SearchAction",
          target: `${BASE_URL}/?q={search_term_string}`,
          "query-input": "required name=search_term_string",
        },
      };
    ld.textContent = JSON.stringify(jsonLd);
  }, [location.pathname]);

  return (
    <Routes>
      <Route path="/" element={<SmartSchoolSystem />} />

      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/terms-conditions" element={<TermsConditions />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/contact-us" element={<ContactUs />} />
      <Route path="/product" element={<Product />} />
      <Route path="/smart-school-system" element={<SmartSchoolSystem />} />
      {/* <Route path="/chat" element="" /> */}
    </Routes>
  );
}

export default App;
